/**
 * Structured error type for API responses
 */
export interface AppError {
  message: string;
  code?: string; // e.g., "UNAUTHORIZED", "NOT_FOUND", "VALIDATION_ERROR"
  status?: number; // HTTP status code
}

/**
 * Unified API result type - replaces all variant patterns
 */
export type ApiResult<T> =
  | { success: true; data: T }
  | { success: false; error: AppError };

/**
 * Type guard to check if an unknown value is an AppError
 */
export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as AppError).message === "string"
  );
}

/**
 * Helper to create an AppError
 */
export function createAppError(
  message: string,
  status?: number,
  code?: string
): AppError {
  return { message, status, code };
}

/**
 * Maps HTTP status codes to error codes
 */
export function getErrorCodeFromStatus(status: number | undefined): string {
  switch (status) {
    case 400:
      return "BAD_REQUEST";
    case 401:
      return "UNAUTHORIZED";
    case 403:
      return "FORBIDDEN";
    case 404:
      return "NOT_FOUND";
    case 409:
      return "CONFLICT";
    case 422:
      return "VALIDATION_ERROR";
    case 429:
      return "RATE_LIMITED";
    case 500:
      return "SERVER_ERROR";
    case 502:
    case 503:
    case 504:
      return "SERVICE_UNAVAILABLE";
    default:
      return "UNKNOWN_ERROR";
  }
}
